/**
 * React hooks for customer risk profile management
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import type { RiskProfileClient } from './client';
import type { CustomerProfile } from './types';

// ============================================================================
// useCustomerProfile Hook
// ============================================================================

export interface UseCustomerProfileOptions {
  autoFetch?: boolean;
}

export interface UseCustomerProfileResult {
  profile: CustomerProfile | null;
  loading: boolean;
  error: Error | null;
  fetchProfile: () => Promise<CustomerProfile>;
  updateProfile: (updates: Partial<CustomerProfile>) => Promise<CustomerProfile>;
  refresh: () => Promise<CustomerProfile>;
}

/**
 * Hook for managing customer risk profiles
 *
 * @param client - RiskProfileClient instance
 * @param customerId - Customer ID to fetch profile for
 * @param options - Hook options
 *
 * @example
 * ```typescript
 * const { profile, loading, error, updateProfile } = useCustomerProfile(
 *   riskClient,
 *   'CUST-12345',
 *   { autoFetch: true }
 * );
 *
 * if (loading) return <Spinner />;
 * if (error) return <Error message={error.message} />;
 * if (!profile) return <NotFound />;
 *
 * return (
 *   <div>
 *     <h1>{profile.full_name}</h1>
 *     <RiskScore score={profile.risk_score} category={profile.risk_category} />
 *   </div>
 * );
 * ```
 */
export function useCustomerProfile(
  client: RiskProfileClient,
  customerId: string,
  options: UseCustomerProfileOptions = {}
): UseCustomerProfileResult {
  const [profile, setProfile] = useState<CustomerProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await client.getProfile(customerId);
      setProfile(result);
      return result;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to fetch profile');
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [client, customerId]);

  const profileRef = useRef(profile);
  profileRef.current = profile;

  const updateProfile = useCallback(
    async (updates: Partial<CustomerProfile>) => {
      if (!profileRef.current) throw new Error('Profile not loaded');

      setLoading(true);
      setError(null);

      try {
        const updated = await client.updateProfile(profileRef.current.id, updates);
        setProfile(updated);
        return updated;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to update profile');
        setError(error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [client]
  );

  useEffect(() => {
    if (options.autoFetch && customerId) {
      fetchProfile();
    }
  }, [options.autoFetch, customerId, fetchProfile]);

  return {
    profile,
    loading,
    error,
    fetchProfile,
    updateProfile,
    refresh: fetchProfile,
  };
}

